import type { AuthenticatedGateClient } from '../servers/GateServer.js';
import type { GateStateStore } from './GateStateStore.js';
import { normalizeStringAuthProps, normalizeStringAuthValue } from './authRecordEncoding.js';

/**
 * In-memory `GateStateStore` — the default, no-redis backing.
 *
 * Optionally WRAPS externally-supplied collections (`authenticatedClients`,
 * `identityMap`) rather than always owning its own — Task 3 passes
 * `GateServer`'s existing `authenticatedClients` object and `identityMap`
 * Map straight in, so any existing test that pokes
 * `gate.authenticatedClients[id] = ...` directly keeps reading/writing the
 * exact same backing this store reads from.
 *
 * publicId ownership: unlike `authenticatedClients`/`identityMap`, today's
 * memory-mode `GateServer` has no independent `pubIdMap`-equivalent at all —
 * `getClientByPublicId`'s memory branch derives ownership by scanning
 * `authenticatedClients` for a matching `publicId` field, and there is no
 * memory-mode operation that clears ownership without deleting the whole
 * record. `GateStateStore`, however, exposes `setPublicIdOwner`/
 * `getPublicIdOwner`/`deletePublicIdOwner` as independent operations (mirroring
 * redis's separate `pubIdMap` hash), and the conformance suite pins that
 * `deletePublicIdOwner` clears ownership WITHOUT touching the auth record
 * (`gateStateStore.conformance.ts`'s "publicId ownership resolves and
 * clears" test never calls `deleteAuthRecord`). A pure scan can never
 * satisfy that — the record, and its `publicId` field, are still there. So
 * this store keeps its own explicit `publicId -> privateId` index, written
 * by `setPublicIdOwner` and cleared by `deletePublicIdOwner`, exactly
 * parallel to `RedisGateStateStore`'s `pubIdMap` hash. See
 * task-2-report.md for the full note — this is a deliberate deviation from
 * the brief's "derives by scan" description, not an oversight.
 *
 * publicId/gameId TYPE: this store keeps the caller's own record object, so a
 * numeric `publicId` or `gameId` is still a number when it is read back — no
 * restoration step, and nothing to keep in sync. That is the behaviour
 * `RedisGateStateStore` has to reproduce with its `publicIdType`/`gameIdType`
 * companion fields (see `authRecordEncoding.ts`), and the conformance suite's
 * "a NUMERIC publicId survives setAuthRecord -> getAuthRecord with its TYPE
 * intact" and "a NUMERIC gameId survives the round trip with its TYPE intact"
 * cases assert it HERE too rather than assuming it — the record shape they
 * check (no extra fields) is what pins that the redis markers never leak
 * upward.
 *
 * Note the ownership INDEX is still `String()`-keyed on both backings
 * (`pubIdOwners`, `pubIdMap`) — the key and the record field are different
 * things, and only the field carries a type.
 */
export class MemoryGateStateStore implements GateStateStore {
    private readonly authenticatedClients: { [privateId: string]: AuthenticatedGateClient<any> };
    private readonly identityMap: Map<string, string>;
    private readonly pubIdOwners: Map<string, string> = new Map();

    // Phase-2 state — NEW to this store (not wrapped/shared with GateServer
    // yet; Tasks 5-6 wire adoption). Kept here, not on RedisGateStateStore,
    // because the redis store is deliberately stateless (the gate rebuilds
    // it per access) — this instance IS the gate-scoped memoization point in
    // memory mode.
    private readonly areasByClient: Map<string, Map<string, number>> = new Map();
    private readonly graceHolds: Map<string, { connectorId: string }> = new Map();
    private readonly seqIds: number[];
    private readonly seqCounter: { get(): number, set(v: number): void };
    private readonly reservingSeats: Map<string, string> = new Map();
    private readonly connectorClients: Map<string, Set<string>> = new Map();

    constructor(opts?: {
        authenticatedClients?: { [privateId: string]: AuthenticatedGateClient<any> },
        identityMap?: Map<string, string>,
        /**
         * The recycled-publicId pool. WRAPPED, on the same rule as
         * `authenticatedClients`/`identityMap` above: `GateServer` passes its
         * own `unusedSequentialIds` array in, so that public field keeps
         * showing the live pool on a memory gate instead of becoming a dead,
         * permanently-empty relic of the pre-store code. A redis gate uses
         * `RedisGateStateStore`'s `seqIds` list and never touches it.
         */
        seqIds?: number[],
        /**
         * The MINT counter, wrapped by ACCESSOR rather than by reference —
         * `GateServer.lastPublicIdSeq` is a number, and a number cannot be
         * shared the way the `seqIds` array is. Passing get/set keeps that
         * field the single source of truth on a memory gate (so a test that
         * pokes it still steers the next mint, and reading it still tells the
         * truth), while `nextSequentialId` becomes the one place the increment
         * happens on both backings.
         */
        seqCounter?: { get(): number, set(v: number): void },
    }) {
        this.authenticatedClients = opts?.authenticatedClients ?? {};
        this.identityMap = opts?.identityMap ?? new Map();
        this.seqIds = opts?.seqIds ?? [];
        let own = 1;
        this.seqCounter = opts?.seqCounter ?? { get: () => own, set: (v: number) => { own = v; } };
    }

    /**
     * MUST STAY AWAIT-FREE. `GateServer.getUserAuth` returns this promise
     * DIRECTLY (it is deliberately not `async`) so that `await getUserAuth(...)`
     * settles in exactly ONE microtask, the way the old synchronous memory
     * branch did. An `await` added inside here costs extra ticks on every auth
     * read and re-orders the purge-vs-reservation races
     * `GateServer.reconnect.test.ts` drives — which fail with a message that
     * says nothing about scheduling. Mirror of the note on `getUserAuth`.
     */
    async getAuthRecord(privateId: string): Promise<AuthenticatedGateClient<any> | undefined> {
        return this.authenticatedClients[privateId];
    }

    async setAuthRecord(privateId: string, record: AuthenticatedGateClient<any>): Promise<void> {
        // `connectorId` is stored as the string it is DECLARED as, on both
        // backings — see `STRING_NORMALIZED_AUTH_PROPS`. In place, deliberately:
        // this store keeps the caller's own object, and the gate and the store
        // must not disagree about the field's type. Redis-side mirror in
        // `RedisGateStateStore.setAuthRecord`.
        normalizeStringAuthProps(record);
        this.authenticatedClients[privateId] = record;
    }

    async updateAuthField(privateId: string, key: string, value: any): Promise<void> {
        const existing = this.authenticatedClients[privateId];
        if (!existing) return;
        // Skip undefined-valued fields before writing — matches
        // `RedisGateStateStore.updateAuthField`'s C1-class guard. Without
        // this, the two backings disagreed: redis left the old value in
        // place, memory overwrote it with `undefined`.
        if (value === undefined) return;
        if (key === 'publicId') {
            // Mirrors `RedisGateStateStore.updateAuthField`'s pubIdMap
            // re-point: changing the `publicId` field has to move ownership
            // in `pubIdOwners` too, or the OLD publicId keeps resolving to
            // this privateId here while it stops resolving on redis (found
            // by the reviewer with a scratch test; regression-pinned by
            // `apps/integration-tests/.../ClientToGate.test.ts:80-99`, which
            // runs this exact `updateUserAuth(id, 'publicId', ...)` +
            // `getClientByPublicId` sequence against a memory gate).
            const oldId = existing.publicId;
            if (oldId !== undefined && oldId !== null) {
                this.pubIdOwners.delete(String(oldId));
            }
            this.pubIdOwners.set(String(value), privateId);
        }
        // The OTHER writer of a normalized prop, and the field's REAL primary
        // writer on the live join path: `reserveSeat` persists the granted seat
        // with `updateUserAuth(privateId, 'connectorId', connector.id)` and
        // never touches `setAuthRecord`, so normalizing only there would never
        // fire for it. Redis-side mirror in `RedisGateStateStore`.
        (existing as any)[key] = normalizeStringAuthValue(key, value);
    }

    async deleteAuthRecord(privateId: string): Promise<void> {
        delete this.authenticatedClients[privateId];
    }

    async getPublicIdOwner(publicId: string | number): Promise<string | undefined> {
        return this.pubIdOwners.get(String(publicId));
    }

    async setPublicIdOwner(publicId: string | number, privateId: string): Promise<void> {
        this.pubIdOwners.set(String(publicId), privateId);
    }

    async deletePublicIdOwner(publicId: string | number): Promise<void> {
        this.pubIdOwners.delete(String(publicId));
    }

    async getIdentityOwner(identityKey: string): Promise<string | undefined> {
        return this.identityMap.get(identityKey);
    }

    async setIdentityOwner(identityKey: string, privateId: string): Promise<void> {
        this.identityMap.set(identityKey, privateId);
    }

    async clearIdentityOwner(identityKey: string): Promise<void> {
        this.identityMap.delete(identityKey);
    }

    async addClientArea(privateId: string, areaId: string, graceMs: number): Promise<void> {
        let areas = this.areasByClient.get(privateId);
        if (!areas) {
            areas = new Map();
            this.areasByClient.set(privateId, areas);
        }
        areas.set(areaId, graceMs);
    }

    async removeClientArea(privateId: string, areaId: string): Promise<void> {
        const areas = this.areasByClient.get(privateId);
        if (!areas) return;
        areas.delete(areaId);
        if (areas.size === 0) this.areasByClient.delete(privateId);
    }

    async getClientAreas(privateId: string): Promise<Array<{ areaId: string, graceMs: number }>> {
        const areas = this.areasByClient.get(privateId);
        if (!areas) return [];
        return Array.from(areas.entries()).map(([areaId, graceMs]) => ({ areaId, graceMs }));
    }

    async clearClientAreas(privateId: string): Promise<void> {
        this.areasByClient.delete(privateId);
    }

    async setGraceHold(privateId: string, connectorId: string | number, _ttlMs: number): Promise<void> {
        // ttlMs is a redis-mode-only backstop — the owning gate's own timer
        // is authoritative in memory mode, so it is intentionally unused here.
        this.graceHolds.set(privateId, { connectorId: String(connectorId) });
    }

    async getGraceHold(privateId: string): Promise<{ connectorId: string | number } | undefined> {
        return this.graceHolds.get(privateId);
    }

    /**
     * ATOMIC CLAIM — see the interface. `Map.delete` already answers "was it
     * there when I removed it", which is exactly the redis `DEL`-count
     * semantics, so the two backings agree without any extra bookkeeping.
     */
    async clearGraceHold(privateId: string): Promise<boolean> {
        return this.graceHolds.delete(privateId);
    }

    async releaseSequentialId(id: number): Promise<void> {
        this.seqIds.push(id);
    }

    async acquireSequentialId(): Promise<number | undefined> {
        return this.seqIds.shift();
    }

    /**
     * Post-increment, exactly as `GateServer.makePublicId` did inline before
     * the mint moved here: the FIRST id handed out is 1. Single-process, so
     * "atomic" is free — nothing can interleave between the read and the write
     * (this method is deliberately await-free).
     */
    async nextSequentialId(): Promise<number> {
        const id = this.seqCounter.get();
        this.seqCounter.set(id + 1);
        return id;
    }

    async setReservingSeat(privateId: string, connectorId: string | number): Promise<void> {
        this.reservingSeats.set(privateId, String(connectorId));
    }

    async getReservingSeat(privateId: string): Promise<string | number | undefined> {
        return this.reservingSeats.get(privateId);
    }

    async clearReservingSeat(privateId: string): Promise<void> {
        this.reservingSeats.delete(privateId);
    }

    async addClientToConnector(connectorId: string | number, privateId: string): Promise<void> {
        const key = String(connectorId);
        let clients = this.connectorClients.get(key);
        if (!clients) {
            clients = new Set();
            this.connectorClients.set(key, clients);
        }
        clients.add(privateId);
    }

    async removeClientFromConnector(connectorId: string | number, privateId: string): Promise<void> {
        const key = String(connectorId);
        const clients = this.connectorClients.get(key);
        if (!clients) return;
        clients.delete(privateId);
        if (clients.size === 0) this.connectorClients.delete(key);
    }

    async getConnectorClients(connectorId: string | number): Promise<string[]> {
        const clients = this.connectorClients.get(String(connectorId));
        return clients ? Array.from(clients) : [];
    }

    async clearConnectorClients(connectorId: string | number): Promise<void> {
        this.connectorClients.delete(String(connectorId));
    }
}
